<template>
  <div :class="classes">
    <label v-if="labelAbove" :for="$attrs.id" class="select__label-above">
      {{ labelAbove }}
    </label>
    <select
      v-bind="$attrs"
      :class="selectFieldClasses"
      :value="selected"
      @change="change"
      @input="(e) => setSelected(e.target.value)"
      @click="onClick"
      @blur="onBlur"
    >
      <slot>
        <option v-if="emptyString" value="">{{ emptyString }}</option>
        <template v-for="(option, index) in options">
          <optgroup v-if="option.group" :label="option.group" :key="index + option.group">
            <option
              v-for="(optionGroup, j) in option.options"
              :value="getOptionValue(optionGroup)"
              :key="typeof option === 'object' ? option.value : j + '_' + index"
              v-bind="getOptionAttrs(optionGroup)"
            >
              {{ getOptionLabel(optionGroup) }}
            </option>
          </optgroup>
          <option
            v-else
            :value="getOptionValue(option)"
            :key="typeof option === 'object' ? option.value : index"
            v-bind="getOptionAttrs(option)"
          >
            {{ getOptionLabel(option) }}
          </option>
        </template>
      </slot>
    </select>
    <label ref="floatingPlaceholder" v-if="$attrs.placeholder" :for="$attrs.id" :class="floatingPlaceholderClasses">
      {{ $attrs.placeholder }}
    </label>
    <span :class="selectIconClasses"></span>
  </div>
</template>

<script>
import valueProp from "../../../utils/valueProp.js";
import callExternalFunction from "../../../utils/callExternalFunction";

export default {
  name: "M4Select",
  mixins: [valueProp, callExternalFunction],
  data() {
    return {
      selected: this.modelValue ? this.modelValue : this.value,
      selectMenuOpen: false,
      floatingPlaceholderRows: 1
    };
  },
  props: {
    error: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: [String, Number, Event]
    },
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    emptyString: {
      type: String
    },
    labelAbove: String,
    onChange: Function
  },
  emits: ["update:modelValue", "init", "click", "blur", "change"],
  mounted() {
    this.$nextTick(function () {
      this.$emit("init", this.selected);
    });
    if (this.$refs.floatingPlaceholder && this.$refs.floatingPlaceholder.offsetHeight >= 14) {
      this.floatingPlaceholderRows = Math.floor(this.$refs.floatingPlaceholder.offsetHeight / 11);
    }
  },
  computed: {
    classes() {
      let classes = ["select"];
      if (this.$attrs.placeholder) {
        classes.push("select--floating-placeholder");
      }
      if (this.isDisabled) {
        classes.push("select--disabled");
      }
      if (this.isDisabled || this.labelAbove) {
        classes.push("select--label-above");
      }
      classes.push("select--floating-placeholder-rows-" + this.floatingPlaceholderRows);
      return classes;
    },
    selectFieldClasses() {
      let classes = ["select__field"];
      if (this.error) {
        classes.push("select__field--error");
      }
      if (this.selected) {
        classes.push("select__field--selected");
      }
      return classes;
    },
    selectIconClasses() {
      let classes = ["select__icon"];
      if (this.selectMenuOpen) {
        classes.push("select__icon--open");
      }
      return classes;
    },
    floatingPlaceholderClasses() {
      let classes = ["select__floating-placeholder"];
      if (this.error) {
        classes.push("select__floating-placeholder--error");
      }
      return classes;
    },
    isDisabled() {
      return this.$attrs.disabled !== undefined;
    },
    hasChangeListener() {
      return typeof this.$props.onChange === "function";
    }
  },
  methods: {
    change(e) {
      this.setSelected(e.target.value);
      this.$emit("change", e);
      this.callExtFunc("change", e);
    },
    setSelected(value) {
      this.selected = value;
      this.$emit("update:modelValue", value);
    },
    onClick(e) {
      this.selectMenuOpen = !this.selectMenuOpen;
      this.$emit("click", e);
    },
    onBlur(e) {
      this.selectMenuOpen = false;
      this.$emit("blur", e);
    },
    getOptionValue(option) {
      if (typeof option === "object") {
        return option.value;
      }
      return option;
    },
    getOptionLabel(option) {
      if (typeof option === "object") {
        return option.label;
      }
      return option;
    },
    getOptionAttrs(option) {
      let attrs = {};
      if (option.disabled) {
        attrs.disabled = true;
      }
      return attrs;
    }
  },
  watch: {
    modelValue(value) {
      this.selected = value;
    },
    value(value) {
      this.selected = value;
    }
  }
};
</script>
